import { ComponentRef, Directive, OnDestroy, OnInit, Type } from '@angular/core';
import { Subscription } from 'rxjs';
import { IWizardPluginComponent } from '../interfaces/wizard-plugin-component.interface';
import { IPluginPreviewComponent } from '../interfaces/plugin-preview-component.interface';
import { ResourceConfig } from '../models/configs/resource-config';
import { WizardPluginConfig } from '../models/configs/wizard-plugin-config';
import { BasePluginData } from '../models/wizard-plugin-models/base-plugin-data';
import { DynamicComponentService } from '../decoration-base/dynamic-component.service';
import { ErrorHandlerService } from '../decoration-base/error-handler.service';
import { DecoratedComponentBase } from '../decoration-base/decorated-component-base';
import { DecoratorService } from '../decoration-base/decorator.service';
import { IDecoratedComponent } from '../decoration-base/decorated-component.interface';
import { CLOUD_RESOURCE_KEY } from '../decorators/cloud-resource';
import { WIZARD_PLUGIN_KEY } from '../decorators/wizard-plugin';
import { IShortLogger, Log } from '../services/logger.service';
import { ComponentHostDirective } from '../decoration-base/component-host.directive';

/**
 * Entry for plugin registry information
 */
export interface PluginEntry {
  componentType: Type<IWizardPluginComponent>;
  config: WizardPluginConfig;
}

/**
 * Base component for wizard builders.
 * Provides common functionality for resource wizard workflows
 * with plugin-based steps using the decoration system.
 */
@Directive()
export abstract class WizardBuilderBaseComponent extends DecoratedComponentBase<ResourceConfig> implements OnInit, OnDestroy {
  // Resource information
  protected resourceConfig: ResourceConfig | undefined;
  resourceName: string = '';
  availableResources: { componentType: Type<IDecoratedComponent>, config: ResourceConfig }[] = [];

  // Plugin handling
  plugins: PluginEntry[] = [];
  currentPluginIndex: number = 0;
  pluginData: Record<string, BasePluginData> = {};

  // Optional plugin tracking
  optionalPlugins: Set<string> = new Set();
  enabledPlugins: Set<string> = new Set();

  // Subscriptions
  protected subscription: Subscription = new Subscription();

  constructor(
    decorationService: DecoratorService,
    dynamicService: DynamicComponentService,
    protected errorHandler: ErrorHandlerService,
    logger: IShortLogger<WizardBuilderBaseComponent>
  ) {
    super(decorationService, dynamicService, CLOUD_RESOURCE_KEY, logger);
  }

  ngOnInit(): void {
    // Initialize from route parameters in child classes
  }

  override ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscription.unsubscribe();

    // Call parent cleanup which handles component references
    super.ngOnDestroy();
  }

  /**
   * Load a resource component and discover its plugins
   */
  protected loadResourceComponent(): void {
    this.errorHandler.tryExecute(() => {
      // Get all resource components
      const resourceComponents = this.decorationService.getDecoratedComponents(CLOUD_RESOURCE_KEY);

      this.logger.i('Available resources:', resourceComponents.map(comp => ({
        componentName: comp.name,
        config: this.decorationService.getComponentConfig<ResourceConfig>(comp, CLOUD_RESOURCE_KEY)
      })));

      // Find the resource by name
      let resourceComponent = resourceComponents.find(comp => {
        const config = this.decorationService.getComponentConfig<ResourceConfig>(comp, CLOUD_RESOURCE_KEY);
        return config?.name === this.resourceName;
      });

      // Try case-insensitive search if not found
      if (!resourceComponent) {
        const normalizedName = this.resourceName.toLowerCase().trim();
        resourceComponent = resourceComponents.find(comp => {
          const config = this.decorationService.getComponentConfig<ResourceConfig>(comp, CLOUD_RESOURCE_KEY);
          return config?.name.toLowerCase().trim() === normalizedName;
        });
      }

      if (!resourceComponent) {
        throw new Error(`Resource not found: ${this.resourceName}`);
      }

      // Get resource configuration
      const resourceConfig = this.decorationService.getComponentConfig<ResourceConfig>(resourceComponent, CLOUD_RESOURCE_KEY);
      if (!resourceConfig) {
        throw new Error(`Resource configuration not found for: ${this.resourceName}`);
      }

      const pluginTypes = resourceConfig.plugins || [];

      this.resourceConfig = resourceConfig;

      this.logger.i(`Resource ${resourceConfig.name} requires plugins:`,
        pluginTypes.map(p => p.name || 'Unknown Plugin'));

      // Load plugins based on resource config
      this.loadPluginsFromConfig(pluginTypes);

      // Reset state
      this.currentPluginIndex = 0;
      this.pluginData = {};

      // Load first plugin if available
      if (this.plugins.length > 0) {
        // Use timeout to ensure view is ready
        setTimeout(() => {
          this.loadCurrentPlugin();
        });
      }
    }, 'loadResourceComponent', null);
  }

  /**
   * Load plugins from resource configuration
   */
  protected loadPluginsFromConfig(pluginTypes: Type<IWizardPluginComponent>[]): void {
    this.errorHandler.tryExecute(() => {
      // Reset plugin tracking
      this.plugins = [];
      this.optionalPlugins.clear();
      this.enabledPlugins.clear();

      this.logger.i(`Loading ${pluginTypes.length} plugins from config`);

      // Get all wizard plugin components
      const wizardPluginComponents = this.decorationService.getDecoratedComponents(WIZARD_PLUGIN_KEY);

      // Add plugins based on the resource's plugin requirements
      for (const pluginType of pluginTypes) {
        const typeName = pluginType.name;
        this.logger.i(`Looking for plugin: ${typeName}`);

        // Find plugin by component type name
        let pluginComponent = wizardPluginComponents.find(comp => comp.name === typeName);

        // If not found, try without "Component" suffix
        if (!pluginComponent) {
          const pluginName = typeName.replace(/Component$/, '');
          this.logger.i(`Trying plugin name without Component suffix: ${pluginName}`);

          // Find by configuration name
          pluginComponent = wizardPluginComponents.find(comp => {
            const config = this.decorationService.getComponentConfig<WizardPluginConfig>(comp, WIZARD_PLUGIN_KEY);
            return config?.name === pluginName;
          });
        }

        if (pluginComponent) {
          const pluginConfig = this.decorationService.getComponentConfig<WizardPluginConfig>(pluginComponent, WIZARD_PLUGIN_KEY);
          if (pluginConfig) {
            this.logger.i(`Found plugin: ${pluginConfig.name}`);
            this.plugins.push({
              componentType: pluginComponent as Type<IWizardPluginComponent>,
              config: pluginConfig
            });

            // Track optional plugins
            if (pluginConfig.optional) {
              this.optionalPlugins.add(pluginConfig.name);
              this.enabledPlugins.add(pluginConfig.name);
            }
          }
        } else {
          this.logger.e(`Plugin not found: ${typeName}`);
        }
      }

      this.logger.i(`Loaded ${this.plugins.length} plugins for this resource`);
    }, 'loadPluginsFromConfig', null);
  }

  /**
   * Load the current plugin component
   * This is an abstract method that must be implemented by child classes to use the correct host directive
   */
  protected abstract loadCurrentPlugin(): void;

  /**
   * Load a plugin component by entry into a host
   *
   * @param host The host directive to load the component into
   * @param pluginEntry The plugin registry entry
   * @param pluginData Optional plugin data to set
   * @returns ComponentRef to the loaded component
   */
  protected loadPluginComponent(
    host: ComponentHostDirective,
    pluginEntry: PluginEntry,
    pluginData?: BasePluginData
  ): ComponentRef<IWizardPluginComponent> | null {
    return this.errorHandler.tryExecute(() => {
      // Load the component
      const componentRef = this.loadComponentByType(host, pluginEntry.componentType);

      if (!componentRef) return null;

      // Set data if we have it and the component supports it
      const instance = componentRef.instance as IWizardPluginComponent;
      if (pluginData && instance.setPluginData) {
        instance.setPluginData(pluginData);
      }

      return componentRef;
    }, 'loadPluginComponent', null);
  }

  /**
   * Load a preview component into a host
   *
   * @param host The host directive to load the preview into
   * @param previewComponentType The preview component type
   * @param data The data to pass to the preview component
   * @returns ComponentRef to the loaded preview
   */
  protected loadPreviewComponent(
    host: ComponentHostDirective,
    previewComponentType: Type<IPluginPreviewComponent>,
    data: any
  ): ComponentRef<IPluginPreviewComponent> | null {
    return this.errorHandler.tryExecute(() => {
      // Load the component
      const componentRef = this.loadComponentByType(host, previewComponentType);

      if (!componentRef) return null;

      // Set data
      if (componentRef.instance.setData) {
        componentRef.instance.setData(data);
      }

      return componentRef;
    }, 'loadPreviewComponent', null);
  }

  /**
   * Save data from the current plugin
   */
  protected saveCurrentPluginData(): void {
    this.errorHandler.tryExecute(() => {
      // Get the current plugin component instance from the active component refs
      const pluginInstance = this.getCurrentPluginInstance();

      if (pluginInstance) {
        const pluginData = pluginInstance.getPluginData();
        const pluginName = pluginData.pluginStepName;

        // Store the data
        this.pluginData[pluginName] = pluginData;
        this.logger.i(`Saved data for plugin ${pluginName}:`, pluginData);
      }
    }, 'saveCurrentPluginData', null);
  }

  /**
   * Update preview components with current data
   * This is an abstract method that must be implemented by child classes
   */
  protected abstract updatePreview(): void;

  /**
   * Navigate to the next step
   */
  nextStep(): void {
    this.errorHandler.tryExecute(() => {
      // Save current data
      this.saveCurrentPluginData();

      const pluginInstance = this.getCurrentPluginInstance();
      // Validate before proceeding
      if (pluginInstance && typeof pluginInstance.validate === 'function' && !pluginInstance.validate()) {
        // notify('Please resolve validation errors before proceeding. (Check for duplicate Enquiry No)', 'warning', 3000);
        return; // Prevent going to next step
      }

      if (this.canGoNext()) {
        this.currentPluginIndex++;

        // Use setTimeout to ensure DOM updates
        setTimeout(() => {
          this.loadCurrentPlugin();
        });
      }
    }, 'nextStep', null);
  }

  /**
   * Navigate to the previous step
   */
  previousStep(): void {
    this.errorHandler.tryExecute(() => {
      // Save current data
      this.saveCurrentPluginData();

      if (this.canGoPrevious()) {
        this.currentPluginIndex--;

        // Use setTimeout to ensure DOM updates
        setTimeout(() => {
          this.loadCurrentPlugin();
        });
      }
    }, 'previousStep', null);
  }

  /**
   * Go to a specific plugin step
   */
  goToPlugin(index: number): void {
    this.errorHandler.tryExecute(() => {
      if (index >= 0 && index < this.plugins.length) {
        // Save current data
        this.saveCurrentPluginData();

        this.currentPluginIndex = index;

        // Use setTimeout to ensure DOM updates
        setTimeout(() => {
          this.loadCurrentPlugin();
        });
      }
    }, 'goToPlugin', null);
  }

  /**
   * Toggle a plugin's enabled status
   */
  togglePlugin(pluginName: string): void {
    this.errorHandler.tryExecute(() => {
      if (this.optionalPlugins.has(pluginName)) {
        if (this.enabledPlugins.has(pluginName)) {
          this.enabledPlugins.delete(pluginName);
        } else {
          this.enabledPlugins.add(pluginName);
        }

        // Rebuild plugin list
        this.rebuildActivePlugins();
      }
    }, 'togglePlugin', null);
  }

  /**
   * Rebuild the list of active plugins based on enabled status
   */
  protected rebuildActivePlugins(): void {
    this.errorHandler.tryExecute(() => {
      // Save current plugin data
      this.saveCurrentPluginData();

      // Filter plugins to only include mandatory and enabled optional ones
      const activePlugins = this.plugins.filter(plugin =>
        !plugin.config.optional || this.enabledPlugins.has(plugin.config.name)
      );

      // If current plugin is now disabled, reset to first plugin
      if (this.currentPluginIndex >= activePlugins.length) {
        this.currentPluginIndex = 0;
      }

      this.plugins = activePlugins;

      // Use setTimeout to ensure DOM updates
      setTimeout(() => {
        this.loadCurrentPlugin();
        this.updatePreview();
      });
    }, 'rebuildActivePlugins', null);
  }

  /**
   * Finish the wizard and collect all plugin data
   */
  finishWizard(action: string = ""): void {
    this.errorHandler.tryExecute(() => {
      // Save the final plugin data
      this.saveCurrentPluginData();

      // Log final data
      this.logger.i('Final wizard data:', this.pluginData);

      // Handle completion in child class
      this.onWizardComplete(this.pluginData, action);
    }, 'finishWizard', null);
  }

  /**
   * Handler for wizard completion
   * @param finalData The collected plugin data
   */
  protected abstract onWizardComplete(finalData: { [key: string]: BasePluginData }, action: string): void;

  /**
   * Get the current plugin entry
   */
  getCurrentPluginEntry(): PluginEntry | null {
    if (this.currentPluginIndex >= 0 && this.currentPluginIndex < this.plugins.length) {
      return this.plugins[this.currentPluginIndex];
    }
    return null;
  }

  /**
   * Get the current plugin component instance
   */
  protected abstract getCurrentPluginInstance(): IWizardPluginComponent | null;

  /**
   * Check if the current plugin is optional
   */
  isCurrentPluginOptional(): boolean {
    const currentPluginEntry = this.getCurrentPluginEntry();
    return currentPluginEntry ? currentPluginEntry.config.optional : false;
  }

  /**
   * Check if a plugin is enabled
   */
  isPluginEnabled(pluginName: string): boolean {
    return this.enabledPlugins.has(pluginName);
  }

  /**
   * Check if we can go to the next step
   */
  canGoNext(): boolean {
    return this.currentPluginIndex < this.plugins.length - 1;
  }

  /**
   * Check if we can go to the previous step
   */
  canGoPrevious(): boolean {
    return this.currentPluginIndex > 0;
  }

  /**
   * Check if there is plugin data
   */
  hasPluginData(): boolean {
    return Object.keys(this.pluginData).length > 0;
  }

  /**
   * Create a preview for plugin data
   *
   * @param pluginEntry The plugin registry entry
   * @param pluginData The plugin data to preview
   * @returns Preview component type or undefined
   */
  protected createPreviewContent(
    pluginEntry: PluginEntry,
    pluginData: BasePluginData
  ): Type<IPluginPreviewComponent> | undefined {
    return this.errorHandler.tryExecute(() => {
      // Create a temporary instance to get the preview content
      const tempInstance = new (pluginEntry.componentType as any)();

      // Set data if possible
      if (tempInstance.setPluginData) {
        tempInstance.setPluginData(pluginData);
      }

      // Get preview content
      if (tempInstance.asPreview) {
        return tempInstance.asPreview();
      }

      return undefined;
    }, 'createPreviewContent', undefined);
  }
}
